import React, { useRef, useState, useCallback, useEffect } from "react";
import Styles from "./Element.module.css";
import Fileupload from "../../../../commons/Inputs/FileUpload/Fileupload";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import { useDispatch, useSelector } from "react-redux";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import {
  getDiscomByStateId,
  getStateByRegionId,
  getIfscDetails, 
} from "../../../../Services/ApiCalling/PublicApi";
import { Add, Close } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { verifyConsumerName } from "../../../../Services/ApiCalling/Auth";
import { decryptData } from "../../../../commons/Decode";
import {
  publicCityById,
  publicDiscomByStateId,
  publicStateByRegionId,
} from "../../../../Redux/Slices/publicSlice";
import { ReactComponent as RemoveIcon } from "../../../../assets/images/Remove.svg";
import { toast } from "react-toastify";
import { toastProperties } from "../../../../commons/Toast/Toaster";
import {
  TAX_CONFIG,
  TAX_FIELDS,
  checkTaxInput,
} from "../../../../helpers/validators/taxValidators";
import { findStateInList, findCityInList, getPincodeDetails } from "../../../../helpers/locationHelper";
import { 
  sanitizePincode, 
  sanitizeMobile, 
  sanitizeContactName, 
  sanitizeIfsc,
  checkMobileStart,
  MOBILE_REGEX,
  CONTACT_NAME_REGEX,
  PINCODE_REGEX,
  IFSC_REGEX,
  ACCOUNT_NUMBER_REGEX,
  GENERAL_ERRORS,
  sanitizeAccountNumber,
  sanitizeGeneralText
} from "../../../../helpers/validators/generalValidators";

const AddressSection = ({
  prefix,
  addressData,
  handleChange,
  errors,
  publicData,
  index,
  unlockField,
  pincodeLoading,
  pincodeErrors,
  autoFilledFields,
}) => {
  const dispatch = useDispatch();
  const [citiesList, setCitiesList] = useState([]);
  const selectedStateId = addressData[`${prefix}_state`]?.id;

  useEffect(() => {
    if (selectedStateId) {
      dispatch(publicCityById(selectedStateId)).then((action) => {
        if (action.payload) {
          setCitiesList(action.payload);
        }
      });
    } else {
      setCitiesList([]);
    }
  }, [selectedStateId, dispatch, prefix]);

  const handleGetCityByStateId = (index, field, value) => {
    handleChange(index, field, value);
    const cityField = field.replace("_state", "_city");
    handleChange(index, cityField, {});
  };

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => {
    const isAutoFilled = autoFilledFields?.[index]?.has(field);
    let maxLength = undefined;
    if (field.endsWith("_address")) maxLength = 250;
    if (field.endsWith("_contact")) maxLength = 50;
    if (field.endsWith("_pincode")) maxLength = 6;
    if (field.endsWith("_mobile") || field.endsWith("_altMobile")) maxLength = 10;

    return (
      <div style={{ position: "relative"}}>
        <TextInput
          heading={`${heading} `}
          value={addressData[field]}
          onChange={(value) => handleChange(index, field, value)}
          maxLength={maxLength}
          example={example}
          type={type}
          height="45"
          error={errors[`${field}_${index}`]}
          readOnly={isAutoFilled || readOnly}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(index, field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px",
              zIndex: 10,
            }}
          >
            🖊 Edit
          </span>
        )}
        {field.endsWith("_pincode") && pincodeLoading?.[`${field}_${index}`] && (
          <span style={{ fontSize: "11px", color: "var(--primary-color-text)", marginTop: "4px", display: "block" }}>
            🔍 Looking up pincode...
          </span>
        )}
        {field.endsWith("_pincode") && pincodeErrors?.[`${field}_${index}`] && (
          <span style={{ fontSize: "11px", color: "var(--warning-text)", marginTop: "4px", display: "block" }}>
            ⚠️ {pincodeErrors[`${field}_${index}`]}
          </span>
        )}
      </div>
    );
  };

  const renderSelectInput = (field, heading, list, example) => {
    const isAutoFilled = autoFilledFields?.[index]?.has(field);
    return (
      <div style={{ position: "relative"}}>
        <SelectInput
          onChange={(value) =>
            heading === "State *"
              ? handleGetCityByStateId(index, field, value)
              : handleChange(index, field, value)
          }
          list={list}
          value={addressData[field]}
          example={example}
          heading={`${heading} `}
          height="45px"
          error={errors[`${field}_${index}`]}
          disabled={isAutoFilled}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(index, field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px",
              zIndex: 10,
            }}
          >
            🖊 Edit
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={Styles.layer1ElementsContainer}>
        {renderTextInput(`${prefix}_address`, "Full Address *", `Enter Address`)}
      </div>
      <div className={Styles.layer3ElementsContainer}>
        {renderTextInput(`${prefix}_pincode`, "Pin Code *", "Enter Pincode")}
        {renderSelectInput(
          `${prefix}_state`,
          "State *",
          publicData?.stateUT,
          "Eg. Select State",
        )}
        {renderSelectInput(
          `${prefix}_city`,
          "City *",
          citiesList,
          "Eg. Select City",
        )}
      </div>

      <div className={Styles.layer2ElementsContainer}>
        {renderTextInput(
          `${prefix}_contact`,
          "Name of the Contact Person *",
          "Enter Contact Person Name",
        )}
        {renderTextInput(`${prefix}_email`, "Email *", "Enter Email")}
      </div>
      <div className={Styles.layer2ElementsContainer}>
        {renderTextInput(`${prefix}_mobile`, "Mobile No. *", "Enter Mobile No.")}
        {renderTextInput(
          `${prefix}_altMobile`,
          "Alternate Mobile No.",
          "Enter Alternate Mobile No.",
        )}
      </div>
    </>
  );
};


const ConsumerDetails = ({ userData, handleData, handleProceed }) => {
  const dispatch = useDispatch();
  const [addressData, setAddressData] = useState({});
  const [sameCheck, setSameCheck] = useState("same");

  const initialConsumerDetail = useCallback(() => {
    const defaultDetail = userData?.consumerDetails?.[0] || {};
    return {
      plantName: defaultDetail.plantName || "",
      maxDrawal: defaultDetail.maxDrawal || "",
      poi: defaultDetail.poi || "",
      specification: defaultDetail.specification || {},
      region: defaultDetail.region || {},
      state: defaultDetail.state || {},
      discom: defaultDetail.discom || [],
      discomName: defaultDetail.discomName || {},
      discomVoltage: defaultDetail.discomVoltage || [],
      istsVoltage: defaultDetail.istsVoltage || [],
      stuVoltage: defaultDetail.stuVoltage || [],
      stateList: defaultDetail.stateList || [],
      discomList: defaultDetail.discomList || [],
      bankName:
        defaultDetail?.bankName || userData?.userCompany?.bankName || "",
      payee: defaultDetail?.payee || userData?.userCompany?.payee || "",
      branchAddress:
        defaultDetail?.bankAddress || userData?.userCompany?.bankAddress || "",
      accountNumber:
        defaultDetail?.accountNo || userData?.userCompany?.accountNo || "",
      ifscCode: defaultDetail?.ifsc || userData?.userCompany?.ifsc || "",
      bankDifferent: false,
      pan: defaultDetail?.pan || userData?.userCompany?.pan || "",
      gst: defaultDetail?.gst || userData?.userCompany?.gst || "",
      tan: defaultDetail?.tan || userData?.userCompany?.tan || "",
      panDocId: defaultDetail?.panDocId || userData?.documentList?.pan || "",
      gstDocId: defaultDetail?.gstDocId || userData?.documentList?.gst || "",
      tanDocId: defaultDetail?.tanDocId || userData?.documentList?.tan || "",

      registered_address: userData?.address?.[0]?.address || "",
      registered_state: userData?.address?.[0]?.state || {},
      registered_city: userData?.address?.[0]?.city || {},
      registered_pincode: userData?.address?.[0]?.pincode || "",
      registered_tel: userData?.address?.[0]?.tel || "",
      registered_email: userData?.address?.[0]?.email || "",
      registered_fax: userData?.address?.[0]?.fax || "",
      registered_contact: userData?.address?.[0]?.contact || "",
      registered_contactPhone: userData?.address?.[0]?.contactPhone || "",
      registered_mobile:
        userData?.address?.[0]?.mobile || userData?.phone || "",
      registered_altMobile:
        userData?.address?.[0]?.altMobile || userData?.phone || "",
      correspondence_address: userData?.address?.[1]?.address || "",
      correspondence_state: userData?.address?.[1]?.state || {},
      correspondence_city: userData?.address?.[1]?.city || {},
      correspondence_pincode: userData?.address?.[1]?.pincode || "",
      correspondence_tel: userData?.address?.[1]?.tel || "",
      correspondence_email: userData?.address?.[1]?.email || "",
      correspondence_fax: userData?.address?.[1]?.fax || "",
      correspondence_contact: userData?.address?.[1]?.contact || "",
      correspondence_contactPhone: userData?.address?.[1]?.contactPhone || "",
      correspondence_mobile:
        userData?.address?.[1]?.mobile || userData?.phone || "",
      correspondence_altMobile:
        userData?.address?.[1]?.altMobile || userData?.phone || "",
    };
  }, [userData]);

  const publicData = useSelector((state) => state?.publicSlice);
  const debounceTimeout = useRef(null);
  const [ifscLoading, setIfscLoading] = useState({});
  const [ifscErrors, setIfscErrors] = useState({});
  const latestIfscSearch = useRef({});

  // State management
  const [totalLength, setTotalLength] = useState(["Consumer Details"]);
  const [errors, setErrors] = useState({});

  const handleSameCheckChange = (value) => {
    setSameCheck(value);
    if (value === "same") {
      setConsumerDetails((prev) => {
        return prev.map((detail) => {
          const updated = { ...detail };
          Object.keys(detail).forEach((key) => {
            if (key.startsWith("registered_")) {
              const corrKey = key.replace("registered_", "correspondence_");
              updated[corrKey] = detail[key];
            }
          });
          return updated;
        });
      });
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        Object.keys(updatedErrors).forEach((key) => {
          if (key.startsWith("correspondence_")) {
            delete updatedErrors[key];
          }
        });
        return updatedErrors;
      });
    }
  };
  const [pincodeLoading, setPincodeLoading] = useState({});
  const [pincodeErrors, setPincodeErrors] = useState({});
  const abortControllers = useRef({});
  const latestPincodeSearch = useRef({});
  const lastLookupPin = useRef({});

  const [autoFilledFields, setAutoFilledFields] = useState({});
  const autoFilledFieldsRef = useRef(autoFilledFields);
  useEffect(() => {
    autoFilledFieldsRef.current = autoFilledFields;
  }, [autoFilledFields]);

  const addAutoFilledFields = (index, fieldsArray) => {
    setAutoFilledFields((prev) => {
      const next = { ...prev };
      const currentSet = new Set(next[index] ?? []);
      fieldsArray.forEach((field) => currentSet.add(field));
      next[index] = currentSet;
      return next;
    });
  };

  const removeAutoFilledFields = (index, fieldsArray) => {
    setAutoFilledFields((prev) => {
      const next = { ...prev };
      const currentSet = new Set(next[index] ?? []);
      fieldsArray.forEach((field) => currentSet.delete(field));
      next[index] = currentSet;
      return next;
    });
  };

  const unlockField = (index, fieldName) => {
    removeAutoFilledFields(index, [fieldName]);
  };

  const triggerPincodeLookup = async (index, type, pincode) => {
    const prefix = type.split("_")[0]; // "registered" or "correspondence"
    const stateField = `${prefix}_state`;
    const cityField = `${prefix}_city`;
    const lookupKey = `${type}_${index}`;

    if (
      lastLookupPin.current[lookupKey] === pincode ||
      latestPincodeSearch.current[lookupKey] === pincode
    ) {
      return;
    }

    if (abortControllers.current[lookupKey]) {
      abortControllers.current[lookupKey].abort();
    }

    const controller = new AbortController();
    abortControllers.current[lookupKey] = controller;
    latestPincodeSearch.current[lookupKey] = pincode;

    setPincodeLoading((prev) => ({ ...prev, [lookupKey]: true }));
    setPincodeErrors((prev) => {
      const next = { ...prev };
      delete next[lookupKey];
      return next;
    });

    try {
      const data = await getPincodeDetails(pincode, controller.signal);
      const postOffices = data[0].PostOffice;
      const stateName = postOffices[0].State;

      const matchedState = findStateInList(stateName, publicData?.stateUT);

      if (matchedState) {
        const citiesAction = await dispatch(publicCityById(matchedState.id));
        const citiesList = citiesAction.payload;

        if (latestPincodeSearch.current[lookupKey] !== pincode) return;

        const matchedCity = findCityInList(postOffices, citiesList);

        const finalState = matchedState;
        const finalCity = matchedCity;

        setConsumerDetails((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            [stateField]: finalState,
            [cityField]: finalCity,
          };
          return updated;
        });

        addAutoFilledFields(index, [stateField, cityField]);

        lastLookupPin.current[lookupKey] = pincode;

        setErrors((prevErrors) => {
          const nextErrors = { ...prevErrors };
          delete nextErrors[`${stateField}_${index}`];
          delete nextErrors[`${cityField}_${index}`];
          return nextErrors;
        });
      } else {
        setPincodeErrors((prev) => ({ 
          ...prev, 
          [lookupKey]: "Couldn't automatically determine the state. Please select it manually." 
        }));
        delete lastLookupPin.current[lookupKey];
      }
    } catch (err) {
      if (err.name !== "CanceledError") {
        setPincodeErrors((prev) => ({ 
          ...prev, 
          [lookupKey]: err.message || "Failed to look up Pincode. Please select state and city manually." 
        }));
        delete lastLookupPin.current[lookupKey];
      }
    } finally {
      if (latestPincodeSearch.current[lookupKey] === pincode) {
        setPincodeLoading((prev) => {
          const next = { ...prev };
          delete next[lookupKey];
          return next;
        });
        delete latestPincodeSearch.current[lookupKey];
      }
    }
  };

  const triggerIfscLookup = async (index, ifscCode) => {
    if (latestIfscSearch.current[index] === ifscCode) return;
    latestIfscSearch.current[index] = ifscCode;

    setIfscLoading((prev) => ({ ...prev, [index]: true }));

    try {
      const res = await getIfscDetails(ifscCode);
      if (latestIfscSearch.current[index] !== ifscCode) return;

      // if (res && res.data && res.data.code === 200) {
      //   const data = res.data.data;
      //   if (!data || !data.bank || !data.branch) {
      //     setIfscErrors((prev) => ({ ...prev, [index]: "Invalid IFSC — check and try again" }));
      //     return;
      //   }

      //   const { bank, branch } = data;

      //   setConsumerDetails((prev) => {
      //     const updated = [...prev];
      //     updated[index] = {
      //       ...updated[index],
      //       bankName: bank,
      //       branchAddress: branch,
      //     };
      //     return updated;
      //   });

      //   addAutoFilledFields(index, ["bankName", "branchAddress"]);

      //   setErrors((prevErrors) => {
      //     const nextErrors = { ...prevErrors };
      //     delete nextErrors[`bankName_${index}`];
      //     delete nextErrors[`branchAddress_${index}`];
      //     return nextErrors;
      //   });
      // } else {
      //   const errorMsg = res?.data?.message || res?.message || "Could not fetch bank details.";
      //   setIfscErrors((prev) => ({ ...prev, [index]: errorMsg }));
      // }
    } catch (err) {
      setIfscErrors((prev) => ({ ...prev, [index]: "Error looking up IFSC." }));
    } finally {
      setIfscLoading((prev) => ({ ...prev, [index]: false }));
    }
  };


  const [consumerDetails, setConsumerDetails] = useState(
    userData?.consumerDetails?.length
      ? userData.consumerDetails
      : [initialConsumerDetail()],
  );

  const consumerDetailsRef = useRef(consumerDetails);
  useEffect(() => {
    consumerDetailsRef.current = consumerDetails;
  }, [consumerDetails]);

  const [discomVoltage, setDiscomVoltage] = useState({
    discomId: {},
    voltage: {},
  });
  const formatFieldName = (field) =>
    field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

    const handleFieldChange = (index, field, value) => {
    let formattedValue = value;
    if (field === "registered_pincode" || field === "correspondence_pincode") {
      formattedValue = value.replace(/[^0-9]/g, "").slice(0, 6);
    }
    if (field === "registered_contact" || field === "correspondence_contact") {
      formattedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    const updatedDetails = [...consumerDetails];
    updatedDetails[index][field] = formattedValue;
    setConsumerDetails(updatedDetails);

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[`${field}_${index}`];
      return newErrors;
    });

    handleData("consumerDetails", updatedDetails);

    if ((field === "registered_pincode" || field === "correspondence_pincode") && formattedValue.length === 6) {
      triggerPincodeLookup(index, field, formattedValue);
    }
  };


  const validateFields = () => {
    const consumerErrors = {};

    consumerDetails.forEach((detail, index) => {
      const baseRequiredFields = [
        "plantName",
        "maxDrawal",
        "poi",
        "specification",
        "region",
        "state",
      ];
      const requiredFields = detail.bankDifferent
        ? [
        "plantName",
        "maxDrawal",
        "poi",
        "specification",
        "region",
        "state",
        "bankName",
        "payee",
        "branchAddress",
        "accountNumber",
        "ifscCode",
        "pan",
        "gst",
        "tan",
        "panDocId",
        "gstDocId",
        "tanDocId",
        "registered_address",
        "registered_state",
        "registered_city",
        "registered_pincode",
        "registered_contact",
        "registered_email",
        "registered_mobile",
      ]:baseRequiredFields;

      if (detail.bankDifferent && sameCheck === "different") {
        requiredFields = [
          ...requiredFields,
          "correspondence_address",
          "correspondence_state",
          "correspondence_city",
          "correspondence_pincode",
          "correspondence_contact",
          "correspondence_email",
          "correspondence_mobile",
        ];
      }
      requiredFields.forEach((field) => {
         if (
          !detail[field] ||
          (typeof detail[field] === "object" && !detail[field]?.id)
        ) {
          consumerErrors[`${field}_${index}`] = `${formatFieldName(
            field,
          )} is required`;
        }
      });
       if (detail.poi === "DISCOM" && (!detail.discomName || !detail.discomName.discomId)) {
        consumerErrors[`discomId_${index}`] = "Discom is required";
      }

      // --- DISCOM COMBINATION VALIDATION ---
      // Only validate DISCOM combinations when POI is set to "DISCOM"
      // if (detail.poi === "DISCOM") {
      //   if (
      //     !detail.discom ||
      //     !Array.isArray(detail.discom) ||
      //     detail.discom.length === 0
      //   ) {
      //     consumerErrors[`discom_${index}`] =
      //       "At least one State-Discom-Voltage combination is required";
      //   } else {
      //     const getDiscomId = (obj) => obj?.id ?? obj?.discomId;
      //     const invalidCombos = detail.discom.filter(
      //       (combo) =>
      //         !combo.state?.id ||
      //         !getDiscomId(combo.discomId) ||
      //         !combo.voltage?.id,
      //     );
      //     if (invalidCombos.length > 0) {
      //       consumerErrors[`discom_${index}`] =
      //         "All discom combinations must have State, Discom, and Voltage selected";
      //     }
      //   }
      // }
      if (detail.plantName && detail.plantName.length > 100) {
        consumerErrors[`plantName_${index}`] = "Consumer name cannot exceed 100 characters";
      }

      if (detail.bankDifferent) {
        if (detail.ifscCode && !IFSC_REGEX.test(detail.ifscCode)) {
          consumerErrors[`ifscCode_${index}`] = GENERAL_ERRORS.ifsc;
        }


        if (detail.payee && !CONTACT_NAME_REGEX.test(detail.payee)) {
          consumerErrors[`payee_${index}`] = GENERAL_ERRORS.payee;
        } else if (detail.payee && detail.payee.length > 50) {
          consumerErrors[`payee_${index}`] = "Payee name cannot exceed 50 characters";
        }
        
        if (detail.accountNo && !ACCOUNT_NUMBER_REGEX.test(detail.accountNo)) {
          consumerErrors[`accountNo_${index}`] = GENERAL_ERRORS.accountNumber;
        }

        if (detail.bankName && detail.bankName.length > 100) {
          consumerErrors[`bankName_${index}`] = "Bank name cannot exceed 100 characters";
        }

        if (detail.branchAddress && detail.branchAddress.length > 250) {
          consumerErrors[`branchAddress_${index}`] = "Branch address cannot exceed 250 characters";
        }

        if (detail.registered_address && detail.registered_address.length > 250) {
          consumerErrors[`registered_address_${index}`] = "Address cannot exceed 250 characters";
        }

        if (detail.correspondence_address && detail.correspondence_address.length > 250) {
          consumerErrors[`correspondence_address_${index}`] = "Address cannot exceed 250 characters";
        }
      }

      [
        "registered_mobile",
        "correspondence_mobile",
        "registered_altMobile",
        "correspondence_altMobile"
      ].forEach((field) => {
        if (detail[field] && !MOBILE_REGEX.test(detail[field])) {
          consumerErrors[`${field}_${index}`] = GENERAL_ERRORS.mobile;
        }
      });

      ["registered_contact", "correspondence_contact"].forEach((field) => {
        if (detail[field] && !CONTACT_NAME_REGEX.test(detail[field])) {
          consumerErrors[`${field}_${index}`] = GENERAL_ERRORS.contactName;
        } else if (detail[field] && detail[field].length > 50) {
          consumerErrors[`${field}_${index}`] = "Contact person name cannot exceed 50 characters";
        }
      });

      ["registered_pincode", "correspondence_pincode"].forEach((field) => {
        if (detail[field] && !PINCODE_REGEX.test(detail[field])) {
          consumerErrors[`${field}_${index}`] = GENERAL_ERRORS.pincode;
        }
      });

      TAX_FIELDS.forEach((field) => {
        const value = detail[field];
        const config = TAX_CONFIG[field];
        if (value && config && !config.validate(value)) {
          consumerErrors[`${field}_${index}`] = config.error;
        }
      });
    });
    console.log("Validation Errors:", consumerErrors);
    setErrors(consumerErrors);
    return Object.keys(consumerErrors).length === 0;
  };

    const handleChange = async (index, type, value, fetchFunction) => {
    const updatedConsumerDetails = [...consumerDetails];

    if (type === "poi") {
      const voltageType = `${value.toLowerCase()}Voltage`;
      if (!updatedConsumerDetails[index][voltageType]) {
        updatedConsumerDetails[index][voltageType] = [];
      }
    }

    updatedConsumerDetails[index][type] = value;

    let formattedValue = TAX_FIELDS.includes(type)
      ? checkTaxInput(type, value)
      : value;
    
    if (type === "registered_pincode" || type === "correspondence_pincode") {
      formattedValue = sanitizePincode(formattedValue);  }
    
    if (type === "registered_contact" || type === "correspondence_contact") {
      formattedValue = sanitizeContactName(formattedValue).slice(0, 50);  }

    if (type === "registered_address" || type === "correspondence_address") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 250);
    }

    if (type === "plantName") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 100);
    }

    if (type === "bankName") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 100);
    }

    if (type === "branchAddress") {
      formattedValue = sanitizeGeneralText(formattedValue).slice(0, 250);
    }

    if (type === "ifscCode") {
      formattedValue = sanitizeIfsc(formattedValue);  
      setIfscErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });

      if (IFSC_REGEX.test(formattedValue)) {
        triggerIfscLookup(index, formattedValue);
      }
    }


    if (type === "payee") {
      formattedValue = sanitizeContactName(formattedValue).slice(0, 50);
    }

    if (type === "accountNumber") {
      formattedValue = sanitizeAccountNumber(formattedValue);
    }

    
    let mobileError = "";
    if (
      type === "registered_mobile" ||
      type === "correspondence_mobile" ||
      type === "registered_altMobile" ||
      type === "correspondence_altMobile"
    ) {
      formattedValue = sanitizeMobile(formattedValue);
      mobileError = checkMobileStart(formattedValue);
    }

    updatedConsumerDetails[index][type] = formattedValue;

    if (sameCheck === "same" && type.startsWith("registered_")) {
      const correspondenceField = type.replace(
        "registered_",
        "correspondence_",
      );
      updatedConsumerDetails[index][correspondenceField] = formattedValue;
    }

    if (type === "plantName") {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(async () => {
        const currentName = updatedConsumerDetails[index]?.plantName?.trim();
        if (currentName) {
          const response = await verifyConsumerName(currentName);
          const decryptedData = decryptData(response?.data);

          setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (decryptedData?.code !== 200) {
              newErrors[`plantName_${index}`] = decryptedData?.error;
            } else {
              delete newErrors[`plantName_${index}`];
            }
            return newErrors;
          });
        }
      }, 5000);
    }

    setConsumerDetails(updatedConsumerDetails);
    
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (mobileError) {
        newErrors[`${type}_${index}`] = mobileError;
      } else {
        delete newErrors[`${type}_${index}`];
      }
      return newErrors;
    });

    // Pass updated data to parent component
    handleData("consumerDetails", updatedConsumerDetails);

    if ((type === "registered_pincode" || type === "correspondence_pincode") && formattedValue.length === 6) {
      triggerPincodeLookup(index, type, formattedValue);
    }
  };


    const handleBlur = (index, field) => {
    const config = TAX_CONFIG[field];
    if (!config) return; 

    const value = consumerDetails[index][field];
    if (!value) return; 

    setErrors((prev) => {
      const next = { ...prev };
      const errorKey = `${field}_${index}`;

      if (!config.validate(value)) {
        next[errorKey] = config.error; 
      } else {
        delete next[errorKey];        
      }
      return next;
    });
  };


  const handleDiscomChange = (index, type, value) => {
    if (type === "discomId") {
      setDiscomVoltage((prev) => ({ ...prev, discomId: value, voltage: {} }));
      // Also update discomName in consumerDetails so voltage selection can reference it
      const updatedConDetails = [...consumerDetails];
      updatedConDetails[index].discomName = value;
      setConsumerDetails(updatedConDetails);
      return;
    }

    if (type === "voltage" && value?.id) {
      const updatedConDetails = [...consumerDetails];
      const discomNameObj = consumerDetails[index]?.discomName || {};
      const normalizedDiscomId = {
        id: discomNameObj.discomId || discomNameObj.id,
        acronym: discomNameObj.acronym || discomNameObj.name,
      };

      // Guard: Discom must be selected before adding voltage
      if (!normalizedDiscomId.id) {
        toast.error(
          "Please select a Discom before selecting a Voltage level",
          toastProperties,
        );
        return;
      }

      const discomObject = {
        state: consumerDetails[index].state,
        discomId: normalizedDiscomId,
        voltage: value,
      };

      // Check for duplicate voltage level for the same DISCOM
      const duplicateVoltageForDiscom = updatedConDetails[index].discom?.find(
        (item) =>
          item.discomId?.id === normalizedDiscomId?.id &&
          item.voltage?.id === value?.id,
      );

      if (duplicateVoltageForDiscom) {
        toast.error(
          `Voltage level "${value.level}" is already selected for this DISCOM`,
          toastProperties,
        );
        return;
      }

      updatedConDetails[index].discom = [
        ...(Array.isArray(updatedConDetails[index].discom)
          ? updatedConDetails[index].discom
          : []),
        discomObject,
      ];
      setDiscomVoltage({ discomId: {}, voltage: {} });
      setConsumerDetails(updatedConDetails);
      handleData("consumerDetails", updatedConDetails);
    }
  };

  const handleContinue = (data) => {
    if (data === "next" && !validateFields()) return;
    handleData("consumerDetails", consumerDetails);
    handleProceed(data);
  };

  const handleAddUser = () => {
    setTotalLength((prev) => [...prev, "Consumer Details"]);
    setConsumerDetails((prev) => {
      const baseConsumer = prev[0] || initialConsumerDetail();
      const newConsumer = JSON.parse(JSON.stringify(baseConsumer));
      const updatedList = [...prev, newConsumer];
      handleData("consumerDetails", updatedList);
      return updatedList;
    });
  };

  const handleRemoveUser = (index) => {
    if (consumerDetails.length > 1) {
      const updatedDetails = consumerDetails.filter((_, i) => i !== index);
      setConsumerDetails(updatedDetails);
      setTotalLength((prev) => prev.filter((_, i) => i !== index));
      handleData("consumerDetails", updatedDetails);

      const updatedErrors = { ...errors };
      Object.keys(updatedErrors).forEach((key) => {
        if (key.endsWith(`_${index}`)) {
          delete updatedErrors[key];
        }
        if (key.includes("_") && parseInt(key.split("_").pop()) > index) {
          const newKey = key.replace(
            /_\d+$/,
            `_${parseInt(key.split("_").pop()) - 1}`,
          );
          updatedErrors[newKey] = updatedErrors[key];
          delete updatedErrors[key];
        }
      });

      const shiftRefs = (refObject, deletedIndex) => {
        if (!refObject.current) return;
        const updated = {};
        Object.keys(refObject.current).forEach((key) => {
          const parts = key.split("_");
          const idx = parseInt(parts.pop());
          const baseKey = parts.join("_");
          
          if (idx < deletedIndex) {
            updated[key] = refObject.current[key];
          } else if (idx > deletedIndex) {
            updated[`${baseKey}_${idx - 1}`] = refObject.current[key];
          }
        });
        refObject.current = updated;
      };
      const shiftState = (prev, deletedIndex) => {
        const updated = {};
        Object.keys(prev).forEach((key) => {
          const parts = key.split("_");
          const idx = parseInt(parts.pop());
          const baseKey = parts.join("_");
          
          if (idx < deletedIndex) {
            updated[key] = prev[key];
          } else if (idx > deletedIndex) {
            updated[`${baseKey}_${idx - 1}`] = prev[key];
          }
        });
        return updated;
      };
      shiftRefs(lastLookupPin, index);
      shiftRefs(latestPincodeSearch, index);
      setPincodeLoading(prev => shiftState(prev, index));
      setPincodeErrors(prev => shiftState(prev, index));
      if (abortControllers.current) {
        const updatedControllers = {};
        Object.keys(abortControllers.current).forEach((key) => {
          const parts = key.split("_");
          const idx = parseInt(parts.pop());
          const baseKey = parts.join("_");
          
          if (idx === index) {
            abortControllers.current[key].abort(); // Cancel request for deleted row
          } else if (idx < index) {
            updatedControllers[key] = abortControllers.current[key];
          } else {
            updatedControllers[`${baseKey}_${idx - 1}`] = abortControllers.current[key];
          }
        });
        abortControllers.current = updatedControllers;
      }

      setErrors(updatedErrors);
    }
  };

  useEffect(() => {
    if (Array.isArray(userData?.consumerDetails)) {
      setConsumerDetails(
        userData.consumerDetails.length > 0
          ? userData.consumerDetails
          : [initialConsumerDetail()],
      );
    }
  }, [userData?.consumerDetails, initialConsumerDetail]);

  const handleGetStateByRegionId = (id) => {
    dispatch(publicStateByRegionId(id?.id));
  };
  const handleGetDiscomByStateId = (id) => {
    dispatch(publicDiscomByStateId(id?.id));
  };
 
  const renderTextInput = (
    index,
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => {
    const taxConfig = TAX_CONFIG[field];
    const isAutoFilled = autoFilledFields?.[index]?.has(field);
    const isIfscField = field === "ifscCode";
    const lookupError = isIfscField ? ifscErrors[index] : null;
    const isIfscLoading = isIfscField ? ifscLoading[index] : false;

    let maxLength = undefined;
    if (field === "plantName") maxLength = 100;
    if (field === "payee") maxLength = 50;
    if (field === "bankName") maxLength = 100;
    if (field === "branchAddress") maxLength = 250;
    if (field === "accountNumber") maxLength = 18;
    if (field === "ifscCode") maxLength = 11;
    if (taxConfig?.maxLength) maxLength = taxConfig.maxLength;

    return (
      <div style={{ position: "relative" }}>
        <TextInput
          heading={`${heading}`}
          value={consumerDetails[index][field]}
          onChange={(value) => handleChange(index, field, value)}
          onBlur={() => handleBlur(index, field)}
          maxLength={maxLength}
          example={isIfscLoading ? "Fetching bank details..." : example}
          inputStyle={taxConfig ? { textTransform: "uppercase" } : {}}
          type={type}
          height="45"
          error={errors[`${field}_${index}`] || lookupError}
          readOnly={readOnly || isAutoFilled}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(index, field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px",
              zIndex: 10,
            }}
          >
            🖊 Edit
          </span>
        )}
        {isIfscField && isIfscLoading && (
          <span style={{ fontSize: "11px", color: "var(--primary-color-text)", marginTop: "4px", display: "block" }}>
            🔍 Looking up bank details...
          </span>
        )}
      </div>
    );
  };

  const renderSelectInput = (
    index,
    type,
    list,
    example,
    heading,
    recurring,
    disabled = false,
  ) => (
    <SelectInput
      onChange={(value) =>
        recurring === "discom"
          ? handleDiscomChange(index, type, value)
          : handleChange(
              index,
              type,
              value,
              type === "region"
                ? handleGetStateByRegionId(value)
                : type === "state"
                  ? handleGetDiscomByStateId(value)
                  : null,
            )
      }
      list={list}
      value={discomVoltage[type] || consumerDetails[index][type]}
      example={example}
      heading={`${heading}`}
      height="45px"
      error={errors[`${type}_${index}`]}
      disabled={disabled}
    />
  );

  const renderFileUpload = (index, heading, field, example, type, ID) => (
    <Fileupload
      heading={heading}
      value={consumerDetails[index][field]}
      onChange={(value) => handleFieldChange(index, field, value)}
      height="45"
      example={example}
      type={type}
      id={ID}
      error={errors[`${field}_${index}`]}
      accept=".pdf"
    />
  );


  const renderConsumerDetail = (ele, index) => (
    <div className={Styles.multiLayerContainer} key={index}>
      <div className={`${Styles.alterSection} ${Styles.alterSectionOther}`}>
        {totalLength.length > 1 && (
          <div
            className={Styles.deleteSection}
            onClick={() => handleRemoveUser(index)}
          >
            <RemoveIcon style={{ fontSize: "16px" }} />
          </div>
        )}
        <div
          className={`${Styles.addSection} ${Styles.AddnewGenSection}`}
          onClick={handleAddUser}
        >
          <Add style={{ fontSize: "14px" }} />
          <span className={Styles.addNewHeading}>Add New Consumer</span>
        </div>
      </div>

      {/* Basic Details */}
      <div className={Styles.layer2ElementsContainer}>
        {renderTextInput(index, "plantName", "Consumer Name *", "Eg. GNA Energy")}
        {renderTextInput(
          index,
          "maxDrawal",
          "Maximum Capacity of Drawal (MW) *",
          "Eg. 1000000",
        )}
      </div>

      {/* Specification and Region */}
      <div className={Styles.layer2ElementsContainer}>
        {renderSelectInput(
          index,
          "specification",
          publicData?.specification?.filter((ele) => ele?.type === "CONSUMER"),
          "Select Specification",
          "Specification *",
        )}
        {renderSelectInput(
          index,
          "region",
          publicData?.region,
          "Select Region",
          "Region *",
        )}
      </div>

      {/* State Selection - always visible */}
      <div className={Styles.layer2ElementsContainer}>
        {renderSelectInput(
          index,
          "state",
          publicData?.regionStateUT || publicData?.stateUT,
          "Select State",
          "State *",
        )}
        <div />
      </div>


      {/* Point of Drawal Section */}
      <div className={Styles.layer3ElementsContainer}>
        <div className={Styles.checkBoxElem}>
          <div className={Styles.checkBoxHead}>Point Of Drawal (POD)*</div>
          <div className={Styles.selectCheckElemEent}>
            {["ISTS", "STU", "DISCOM"].map((poiType) => (
              <div
                key={poiType}
                onClick={() => handleChange(index, "poi", poiType)}
                style={{ width: "30%" }}
                className={
                  consumerDetails[index].poi === poiType
                    ? Styles.activeCheck
                    : Styles.inactiveCheck
                }
              >
                {poiType}
              </div>
            ))}
          </div>
          {errors[`poi_${index}`] && (
            <span className={Styles.errorPass}>{errors[`poi_${index}`]}</span>
          )}
        </div>
        <div>
          {consumerDetails[index].poi === "DISCOM" && (
          renderSelectInput(
            index,
            "discomId",
            publicData?.discom || [],
            "Select Discom",
            "Discom *",
            "discom",
            !consumerDetails[index]?.state?.id,
          )
          )}
        </div>
        <div/>
        {/* Voltage Selection */}
        {/* <div>
          {(consumerDetails[index].poi === "ISTS" ||
            consumerDetails[index].poi === "STU") && (
            <SelectInput
              onChange={(value) => {
                const updatedVoltages = [...consumerDetails];
                const voltageType = `${consumerDetails[
                  index
                ].poi.toLowerCase()}Voltage`;

                if (!updatedVoltages[index][voltageType]) {
                  updatedVoltages[index][voltageType] = [];
                }

                // Get current voltage list
                const currentVoltages = updatedVoltages[index][voltageType];

                // Check if voltage is already selected
                const isAlreadySelected = currentVoltages.some(
                  (v) => (v?.id || v) === (value?.id || value),
                );

                if (isAlreadySelected) {
                  // Remove voltage if already selected
                  updatedVoltages[index][voltageType] = currentVoltages.filter(
                    (v) => (v?.id || v) !== (value?.id || value),
                  );
                } else {
                  // Add voltage if not already selected
                  updatedVoltages[index][voltageType] = [
                    ...currentVoltages,
                    value,
                  ];
                }

                setConsumerDetails(updatedVoltages);
                handleData("consumerDetails", updatedVoltages);
              }}
              list={publicData?.voltage}
              value={{}}
              example={"Select Voltage Levels"}
              heading={`${consumerDetails[index].poi} Voltage`}
              height={"45px"}
              multiple
            />
          )}
        </div> */}

        {/* Voltage Display */}
        {/* {(consumerDetails[index].poi === "ISTS" ||
          consumerDetails[index].poi === "STU") && (
          <div className={Styles.layerscrollElements}>
            {(Array.isArray(
              consumerDetails[index][
                `${consumerDetails[index].poi.toLowerCase()}Voltage`
              ],
            )
              ? consumerDetails[index][
                  `${consumerDetails[index].poi.toLowerCase()}Voltage`
                ]
              : []
            ).map((ele, voltageIndex) => (
              <div key={voltageIndex} className={Styles.eachLevelLayer}>
                <span>{ele.level}</span>
                <Close
                  style={{ fontSize: "14px", cursor: "pointer" }}
                  onClick={() => {
                    const updatedVoltages = [...consumerDetails];
                    const voltageType = `${consumerDetails[
                      index
                    ].poi.toLowerCase()}Voltage`;
                    updatedVoltages[index][voltageType] = updatedVoltages[
                      index
                    ][voltageType].filter((_, i) => i !== voltageIndex);
                    setConsumerDetails(updatedVoltages);
                    handleData("consumerDetails", updatedVoltages);
                  }}
                />
              </div>
            ))}
          </div>
        )} */}
      </div>

      <div className={Styles.divider}></div>

      {/* Discom Selection */}
      {/* <div className={Styles.discomSelectionContainer}>
        <div className={Styles.subConterntHeader}>Discom Voltage</div>
        <div className={Styles.layer3ElementsContainer}>
          {renderSelectInput(
            index,
            "state",
            publicData?.stateUT,
            "Select State",
            "State",
          )}
          {renderSelectInput(
            index,
            `discomId`,
            publicData?.discom || [],
            "Select Discom",
            "Discom",
            "discom",
            discomVoltage?.discomId,
          )}
          {renderSelectInput(
            index,
            `voltage`,
            publicData?.voltage,
            "Select Voltage Level",
            "Voltage Level",
            "discom",
            discomVoltage?.voltage,
          )}
        </div>

        <div className={Styles.voltageOverflow}>
          {consumerDetails[index]?.discom?.map((ele, discomIndex) => (
            <div key={discomIndex} className={Styles.voltageSection}>
              <div className={Styles.actVoltageLevel}>
                <div>{ele?.voltage?.level}</div>
                <Close
                  style={{
                    fontSize: "12px",
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const updatedConsumerDetails = [...consumerDetails];
                    updatedConsumerDetails[index].discom =
                      updatedConsumerDetails[index].discom.filter(
                        (_, i) => i !== discomIndex,
                      );
                    setConsumerDetails(updatedConsumerDetails);
                    handleData("consumerDetails", updatedConsumerDetails);
                  }}
                />
              </div>
              <div className={Styles.discomName}>{ele?.discomId?.acronym}</div>
            </div>
          ))}
        </div>
      </div> */}

      {/* Bank Details */}
      <div className={Styles.corrAddressContainer}>
        <div
          className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
        >
          Bank Details
        </div>
        <div className={Styles.corrAddSelection}>
          <div className={Styles.officeSelectionText}>
            <div>
              Same as the <b>Company</b> bank details
            </div>
            <div className={Styles.sel_button}>
              {["Yes", "No"].map((option) => (
                <span
                  key={option}
                  onClick={() => {
                    const updatedConsumerDetails = [...consumerDetails];
                    const isDifferent = option === "No";
                    updatedConsumerDetails[index].bankDifferent = isDifferent;
                    setConsumerDetails(updatedConsumerDetails);
                    handleData("consumerDetails", updatedConsumerDetails);
                    if (!isDifferent) {
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        const fieldsToClear = [
                          "bankName", "branchAddress", "accountNumber", "payee", "ifscCode",
                          "pan", "gst", "tan", "panDocId", "gstDocId", "tanDocId",
                          "registered_address", "registered_state", "registered_city", "registered_pincode",
                          "registered_contact", "registered_email", "registered_mobile", "registered_altMobile",
                          "correspondence_address", "correspondence_state", "correspondence_city", "correspondence_pincode",
                          "correspondence_contact", "correspondence_email", "correspondence_mobile", "correspondence_altMobile"
                        ];
                        fieldsToClear.forEach((f) => {
                          delete newErrors[`${f}_${index}`];
                        });
                        return newErrors;
                      });
                    }
                  }}
                  className={
                    (
                      option === "Yes"
                        ? !consumerDetails[index].bankDifferent
                        : consumerDetails[index].bankDifferent
                    )
                      ? Styles.active_corr
                      : Styles.inAct_corr
                  }
                >
                  {option}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={Styles.divider}></div>

      {consumerDetails[index].bankDifferent && (
        <>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput(
              index,
              "accountNumber",
              "Account Number *",
              "Eg. 1234567890",
              "number",
            )}
            {renderTextInput(index, "payee", "Payee Name *", "Eg. John Doe")}
            {renderTextInput(index, "ifscCode", "IFSC Code *", "Eg. SBIN0001234")}
          </div>
          <div className={Styles.layer2ElementsContainer}>
            {renderTextInput(
              index,
              "bankName",
              "Bank Name *",
              "Eg. State Bank of India",
            )}
            {renderTextInput(
              index,
              "branchAddress",
              "Bank Address *",
              "Eg. 123 Main Street, City",
            )}
          </div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput(index, "gst", "GST Number *", "Eg. 123456789012345")}
            {renderTextInput(index, "pan", "PAN Number *", "Eg. ABCDE1234F")}
            {renderTextInput(index, "tan", "TAN Number *", "Eg. ABCDE1234F")}
          </div>
          <div className={Styles.layer3ElementsContainer}>
            {renderFileUpload(
              index,
              "GST Certificate Copy",
              "gstDocId",
              "Upload GST (.pdf accepted)",
              "GST",
              "gstDocId",
            )}
            {renderFileUpload(
              index,
              "PAN Card Copy",
              "panDocId",
              "Upload PAN (.pdf accepted)",
              "PAN",
              "panDocId",
            )}
            {renderFileUpload(
              index,
              "TAN Card Copy",
              "tanDocId",
              "Upload TAN (.pdf accepted)",
              "TAN",
              "tanDocId",
            )}
          </div>
        </>
      )}

      <div className={Styles.conAddressContainer}>
        <div
          className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
        >
          Registered Address
        </div>
        <div className={Styles.divider}></div>
        <AddressSection
          prefix="registered"
          addressData={consumerDetails[index]}
          handleChange={handleChange}
          errors={errors}
          publicData={publicData}
          index={index}
          autoFilledFields={autoFilledFields}
          unlockField={unlockField}
          pincodeLoading={pincodeLoading}
          pincodeErrors={pincodeErrors}
        />
        <div className={Styles.corrAddressContainer}>
          <div
            className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
          >
            Correspondence Address
          </div>
          <div className={Styles.corrAddSelection}>
            <div className={Styles.officeSelectionText}>
              <div>Same as that of Office Address</div>
              <div className={Styles.sel_button}>
                <span
                  onClick={() => handleSameCheckChange("same")}
                  className={
                    sameCheck === "same"
                      ? Styles.active_corr
                      : Styles.inAct_corr
                  }
                >
                  Yes
                </span>
                <span
                  onClick={() => handleSameCheckChange("different")}
                  className={
                    sameCheck === "different"
                      ? Styles.active_corr
                      : Styles.inAct_corr
                  }
                >
                  No
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={Styles.divider}></div>
        {sameCheck === "different" && (
          <AddressSection
            prefix="correspondence"
            addressData={consumerDetails[index]}
            handleChange={handleChange}
            errors={errors}
            publicData={publicData}
            index={index}
            autoFilledFields={autoFilledFields}
            unlockField={unlockField}
            pincodeLoading={pincodeLoading}
            pincodeErrors={pincodeErrors}
          />
        )}
      </div>

      {(totalLength.length === 0 || totalLength.length === 1) && (
        <div className={Styles.AddMoreCont} onClick={handleAddUser}>
          <Add style={{ fontSize: "14px" }} />
          <span>Add New Consumer</span>
        </div>
      )}
    </div>
  );

  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={`${Styles.conterntHeader} ${Styles.contentHeadGen}`}>
          <span className={Styles.genHeading}>Consumer Details</span>
          <div className={Styles.noteContainer}>
            <b>Note:</b>
            <span>You can Create multiple Consumers </span>
          </div>
        </div>
        <div className={Styles.detailsInpMainContainer}>
          {totalLength.map((ele, index) => renderConsumerDetail(ele, index))}
        </div>
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.prevBtn} onClick={() => handleContinue("prev")}>
          Back
        </div>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default ConsumerDetails;
